import React from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from '../../../shared/components';
import { formatDateTime } from '../../../shared/utils/localization';
import { EmailMessageRendering, getEmailMessageRendering } from '../services/emailComposeService';
import detailsStyles from './ConversationDetails.module.css';
import styles from './EmailComposeModal.module.css';

interface EmailRenderingModalProps {
  conversationId: string;
  messageId: string | null;
  onClose: () => void;
}

/** Shows the exact email that was delivered for an agent reply. */
const EmailRenderingModal: React.FC<EmailRenderingModalProps> = ({ conversationId, messageId, onClose }) => {
  const { t } = useTranslation(['conversations', 'common']);
  const [rendering, setRendering] = React.useState<EmailMessageRendering | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!messageId) {
      return undefined;
    }

    let cancelled = false;
    setRendering(null);
    setError(null);
    getEmailMessageRendering(conversationId, messageId)
      .then(result => { if (!cancelled) setRendering(result); })
      .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : t('emailRenderingLoadFailed')); });
    return () => { cancelled = true; };
  }, [conversationId, messageId, t]);

  if (!messageId) {
    return null;
  }

  return (
    <div className={detailsStyles.modalOverlay} role="dialog" aria-modal="true" aria-label={t('emailRenderingTitle')} onClick={onClose}>
      <div className={`${detailsStyles.modalCard} ${styles.renderingCard}`} onClick={event => event.stopPropagation()}>
        <div className={detailsStyles.modalHeader}>
          <div className={detailsStyles.modalTitle}>{rendering?.subject || t('emailRenderingTitle')}</div>
          <button type="button" className={detailsStyles.modalCloseBtn} onClick={onClose}>
            {t('close', { ns: 'common' })}
          </button>
        </div>
        {error ? <div className={detailsStyles.modalError}>{error}</div> : null}
        {!rendering && !error ? <div className={styles.loading}><Spinner /></div> : null}
        {rendering ? (
          <>
            <div className={styles.renderingMeta}>
              <span>{t('emailComposeTo', { address: rendering.recipient })}</span>
              {rendering.sentAt ? <span>{formatDateTime(rendering.sentAt)}</span> : null}
              {rendering.layoutName ? (
                <span>{t('emailRenderingLayout', { name: rendering.layoutName, version: rendering.layoutVersion ?? 1 })}</span>
              ) : null}
            </div>
            {rendering.html ? (
              <iframe
                title={t('emailRenderingTitle')}
                className={styles.renderingFrame}
                sandbox=""
                srcDoc={rendering.html}
                data-testid="email-rendering-frame"
              />
            ) : (
              <pre className={styles.renderingText}>{rendering.text ?? ''}</pre>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
};

export default EmailRenderingModal;
